import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as demographicsConstants from './demographics-constants';
import { List } from 'immutable';
import { SaveButton, ConfirmModalWindow, ModalWindow, LoadingPane, PagedList, formElements, RequiredIndicator } from '../../common';
import { push } from 'connected-react-router';
import * as demographicsActions from './demographics.actions';
import * as permissionsActions from '../permissions.actions';
import PermissionEdit from '../permission-edit';
import './demographics.scss';
import { Title } from '../../layout';

const emptyGuid = '00000000-0000-0000-0000-000000000000';
export class DemographicsComponent extends Component {

  componentDidMount() {
    const { demographicsActions } = this.props;
    const { fetchSettings, fetchSites, fetchDemographicPermissions } = demographicsActions || {};
    fetchSettings();
    fetchSites();
    fetchDemographicPermissions();
  }

  componentWillUpdate(nextProps, nextState) {
    const { demographicsActions } = this.props;
    const { fetchSettings, fetchSites, fetchDemographicPermissions } = demographicsActions || {};
    fetchSettings();
    fetchSites();
    fetchDemographicPermissions();
  }

  componentDidUpdate(prevProps, prevState) {
    const { demographics : prevDemographics } = prevProps;
    const { showConfirmSettingsModalDemoEnabled : prevEnabledModalEnabled,
            showConfirmSettingsModalDemoDisabled : prevDisabledModalEnabled } = prevDemographics || {};
    const { demographics } = this.props;
    const { showConfirmSettingsModalDemoEnabled, showConfirmSettingsModalDemoDisabled } = demographics || {};

    //for the modal when demographics are already enabled
    if(showConfirmSettingsModalDemoEnabled && !prevEnabledModalEnabled) {
      var enabledModalTarget = document.getElementById('confirm-enabled');
      if(enabledModalTarget) enabledModalTarget.focus();
    }
    else if(!showConfirmSettingsModalDemoEnabled && prevEnabledModalEnabled) {
      var enabledFocusTarget = document.getElementById('view');
      if(enabledFocusTarget) enabledFocusTarget.focus();
    }

    //for the modal that asks the user if it should be enabled or not
    if(showConfirmSettingsModalDemoDisabled && !prevDisabledModalEnabled) {
      var disabledModalTarget = document.getElementById('confirm-disabled');
      if(disabledModalTarget) disabledModalTarget.focus();
    }
    else if(!showConfirmSettingsModalDemoDisabled && prevDisabledModalEnabled) {
      var disabledFocusTarget = document.getElementById('view');
      if(disabledFocusTarget) disabledFocusTarget.focus();
    }
  }

  handleSave() {
    const { demographicsActions } = this.props;
    demographicsActions.saveSettings();
  }

  handleCancel() {
    const { redirect, demographicsActions } = this.props;
    demographicsActions.clearChanges();
    redirect('/permissions');
  }

  handleUpdateSite(siteId) {
    const { demographicsActions } = this.props;
    demographicsActions.updateSite(siteId);
  }

  handleUpdateViewPermission(demographicId, value) {
    const { demographicsActions } = this.props;
    demographicsActions.updatePermission(demographicId, 'view', value);
    if(value === false)
      demographicsActions.updatePermission(demographicId, 'edit', value);
  }

  handleUpdateEditPermission(demographicId, value) {
    const { demographicsActions } = this.props;
    demographicsActions.updatePermission(demographicId, 'edit', value);
  }

  mapDemographicPermission(demographicPermission) {
    const { demographics } = this.props;
    const { permissionsResponse, save } = demographics || {};
    const { isFetching : isSubmitting } = save || {};
    const { demographicId, demographicName, viewPerm, editPerm, disabled, required } = demographicPermission || {};

    if(disabled) {
      return null;
    }

    const permissions =  permissionsResponse && permissionsResponse.get('permissions');
    const permissionsValue = permissions && permissions.find(val => val.demographicId === demographicId);
    const view = permissionsValue && permissionsValue.view;
    const edit = permissionsValue && permissionsValue.edit;

    const viewValue = view === undefined ? viewPerm === undefined ? false : viewPerm : view;

    return (
      <tr key={demographicId}>
        <td>{demographicName}<RequiredIndicator isRequired={required}/></td>
        <td className="view-permission">
          <PermissionEdit
            id='viewPermission'
            ariaLabel='view'
            response={view}
            defaultValue={viewPerm}
            disabled={isSubmitting}
            onChange={value => this.handleUpdateViewPermission(demographicId, value)}/>
        </td>
        <td className="edit-permission">
          <PermissionEdit
            id='editPermission'
            ariaLabel='edit'
            response={edit}
            defaultValue={editPerm}
            disabled={isSubmitting || !viewValue}
            onChange={value => this.handleUpdateEditPermission(demographicId, value)}/>
        </td>
      </tr>
    )
  }

  /**
   *
   * After hitting save, this will display 1 of 2 modals:
   * If demographics are currently enabled for participants, then it will simply ask the user to confirm the update.
   * If demographics are currently not enabled for participants, then it will give the user the option of enabling
   * demographics in the portal before saving.
   */
  handleConfirmSubmit() {
    const { demographics, demographicsActions } = this.props;
    const { settings } = demographics || {};
    const { showConfirmSettingsModalDemoEnabledAction, showConfirmSettingsModalDemoDisabledAction } = demographicsActions;
    const { data : settingsData } = settings || {};
    const { demographicsEnabled } = settingsData || {};

    if(demographicsEnabled) {
      showConfirmSettingsModalDemoEnabledAction(true);
    }
    else {
      showConfirmSettingsModalDemoDisabledAction(true);
    }
  }

  /**
   * If demographics are enabled, then save the updated demographic permissions
   * and redirect to the main permissions page
   * @param {bool} result : true if user hits OK, false if the user hits cancel
   */
  handleConfirmEnabledSave(result) {
    const { demographicsActions, redirect } = this.props;
    const { showConfirmSettingsModalDemoEnabledAction } = demographicsActions;
    if(!!result) {
      this.handleSave();
      redirect('/permissions');
    }

    showConfirmSettingsModalDemoEnabledAction(false);
  }

  /**
   * Saves the settings that enables/disables demographics to the API
   * @param {ClickEvent} e
   */
  handleConfirmDisabledSave() {
    const { demographicsActions, demographics, permissionsActions, redirect } = this.props;
    const { selectedSaveOption } = demographics;
    const { showConfirmSettingsModalDemoDisabledAction, saveSettings } = demographicsActions;

    if(selectedSaveOption === "yes") {
      permissionsActions.submitDemographicsEnabledStatus(true);
    }

    if(selectedSaveOption === "yes" || selectedSaveOption === "no") {
      saveSettings();
      redirect('/permissions');
    }

    showConfirmSettingsModalDemoDisabledAction(false);
  }

  handleConfirmSettingsModalDemoDisabledClose() {
    const { demographicsActions } = this.props;
    const { showConfirmSettingsModalDemoDisabledAction } = demographicsActions;

    showConfirmSettingsModalDemoDisabledAction(false);
  }

  handleSelectionChange(value) {
    const { demographicsActions } = this.props;
    const { setSelectedSaveOption } = demographicsActions;
    const saveOption = !!value && value.size > 0 ? value.get(0) : undefined;
    setSelectedSaveOption(saveOption);
  }

  render() {
    const { demographics, demographicsActions } = this.props;
    const { changePageNumber, changeResultsPerPage } = demographicsActions;
    const { settings, sites, permissionsResponse, allDemographics, demographicsPagingData, showConfirmSettingsModalDemoEnabled,
      showConfirmSettingsModalDemoDisabled, selectedSaveOption } = demographics || {};
    const { itemsPerPage } = demographicsPagingData || {};
    const { isFetching : loadingDemographics, err : demographicsErr, data : demographicsData } = allDemographics || {};
    const { isFetching : loadingSettings, err : settingsErr, data : settingsData } = settings || {};
    const { demographicsEnabled, defaultSite } = settingsData || {};
    const { isFetching : loadingSites, err : sitesErr, data : sitesData } = sites || {};

    var demographicsList = [];
    var currentPage = 1;
    var totalItemsCount = 0;
    if(demographicsData) {
      demographicsList = demographicsData.results;
      currentPage = demographicsData.currentPage;
      totalItemsCount = demographicsData.totalResults;
    }

    const perPageOptions = [
      { value : 5, label : '5' },
      { value : 10, label : '10'},
      { value : 25, label : '25'},
      { value : 50, label : '50'},
      { value : 100, label : '100'}
    ];

    const saveOptions = List([
      { id: 0, value:'yes', label: demographicsConstants.saveOption1 },
      { id: 1, value:'no', label: demographicsConstants.saveOption2 },
      { id: 2, value:'cancel', label: "Cancel"}
    ]);

    const site = permissionsResponse && permissionsResponse.get('defaultSite');
    const currentSiteValue = site !== undefined ? site :  !!defaultSite ? defaultSite : emptyGuid;

    const saveContent =
    <div>
      <h4 className={'save-list'}>{demographicsConstants.saveModalDescrip}</h4><br />
      <div>
        <formElements.RadioElement
          isRequired={false}
          label={demographicsConstants.saveModalLabel}
          options={saveOptions}
          disabled={false}
          defaultValue={List([selectedSaveOption])}
          onChange={(value) => this.handleSelectionChange(value)}
          css={{ buttonLabel: 'save-list', radioButton: 'save-list', label: 'save-list'}} />
      </div>
    </div>

    const saveFooter =
      <div>
        <button
          className="btn btn-primary"
          onClick={e => this.handleConfirmDisabledSave(e)}>OK</button>
      </div>

    return (

      <div id='demographics-settings'>
        <Title title="Demographics Permissions" />
        <h1>{demographicsConstants.title}</h1>
        <p>{demographicsConstants.pageDescription}</p>
          { demographicsEnabled ? null :
            <LoadingPane id="demographics-settings-loader" isLoading={loadingSettings}>
              <p id="enabled-warning" className="alert alert-warning">{demographicsConstants.disabledWarning}</p>
            </LoadingPane>
          }
        <h2>{demographicsConstants.displayingTitle}</h2>
        <p>{demographicsConstants.siteDescription1}</p>
          <LoadingPane
            isLoading={loadingSettings || loadingSites}
            err={settingsErr || sitesErr}
            loadingMessage={"Loading site data"}
            contentLoadedMessage={"site data loaded"}>
            <select
              aria-label="Select Site"
              className="form-control site-dropdown"
              value={currentSiteValue}
              onChange={(e) => this.handleUpdateSite(e.target.value)}>
              <option value={emptyGuid}>--Select--</option>
              {
                sitesData && sitesData.map( site => (
                  <option
                    key={site.id}
                    value={site.id}>
                    {site.name}
                  </option>
                ))
              }
            </select>
          </LoadingPane>
        <p>{demographicsConstants.siteDescription2}</p>
        <h2>{demographicsConstants.permissionsTitle}</h2>
        <p>{demographicsConstants.permissionsDescription}</p>
        <PagedList
          header={(
            <tr>
              <th>NAME</th>
              <th>VIEW</th>
              <th>EDIT</th>
            </tr>
          )}
          items={demographicsList}
          emptyMessage={<p>No Demographics Found</p>}
          mapItem={permission => this.mapDemographicPermission(permission)}
          currentPage={currentPage}
          resultsPerPage={itemsPerPage}
          variableResultsPerPage={true}
          perPageOptions={perPageOptions}
          totalItemsCount={totalItemsCount}
          changePageNumber={page => changePageNumber(page)}
          changeResultsPerPage={numResults => changeResultsPerPage(numResults)}
          isLoading={loadingDemographics}
          loadingMessage='Loading demographic permissions data...'
          contentLoadedMessage={"demographic permissions loaded"}
          loadingErr={demographicsErr} />
        <div className="actions">
            <SaveButton text="Save" onSave={() => this.handleConfirmSubmit()}/>
          <button
            className="btn btn-default"
            onClick={() => this.handleCancel()}>
            Cancel
          </button>
        </div>

        {/* modal for confirming changes while demographics have already been made available  */}
        <ConfirmModalWindow show={showConfirmSettingsModalDemoEnabled}
          title="Updating Demographics Permissions"
          children={<span>{demographicsConstants.confirmModalLabel}</span>}
          onClose={(e, result) => this.handleConfirmEnabledSave(result)}
          contentId="confirm-enabled" />

        {/* modal for letting user choose to make demographics available or not after saving  */}
        <ModalWindow show={showConfirmSettingsModalDemoDisabled}
          title={"Save Permissions"}
          children={saveContent}
          footer={saveFooter}
          onClose={() => this.handleConfirmSettingsModalDemoDisabledClose()}
          contentId="confirm-disabled" />
      </div>
    );
  }
}

DemographicsComponent.propTypes = {
  demographics : PropTypes.object,
  redirect : PropTypes.func,
  demographicsActions : PropTypes.object,
  permissionsActions : PropTypes.object
};

const mapStateToProps = ({ demographics }) => {
  return {
    demographics,
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    redirect: bindActionCreators(push, dispatch),
    demographicsActions: bindActionCreators(demographicsActions, dispatch),
    permissionsActions: bindActionCreators(permissionsActions, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DemographicsComponent);
